/*!
 * @Instruduction: AI 大模型管理服务接口
 * 对应需求文档_模块1_AI大模型管理.md §四 接口契约
 * 路径前缀：/api/ai/provider
 *
 * 注：后端 Spring Boot + Jackson 默认返回驼峰命名（apiKeyMask 等），
 * 本服务层统一转换为前端下划线命名（api_key_mask 等），避免组件层字段漂移。
 */

import xhr from './xhr/index'
import {
    IGetProviderListParams,
    IUpsertProviderParams,
    IAiProviderType
} from '@/types/ai-types'

const BASE = '/api/ai/provider'

/** 后端驼峰 → 前端下划线 字段映射表 */
const CAMEL_TO_SNAKE_MAP: Record<string, string> = {
    apiKey: 'api_key',
    apiKeyMask: 'api_key_mask',
    apiUrl: 'api_url',
    deployMode: 'deploy_mode',
    promptTemplate: 'prompt_template',
    isDefault: 'is_default',
    createdAt: 'created_at',
    updatedAt: 'updated_at',
    createdBy: 'created_by',
    updatedBy: 'updated_by',
    pageTotal: 'page_total',
    pageIndex: 'page_index',
    capabilities: 'capabilities'
}

/** 将对象的驼峰字段批量转为下划线（仅映射表中声明的字段） */
function camelToSnake<T = any>(obj: any): T {
    if (!obj || typeof obj !== 'object') return obj
    const result: any = {}
    for (const key of Object.keys(obj)) {
        const snakeKey = CAMEL_TO_SNAKE_MAP[key] || key
        result[snakeKey] = obj[key]
    }
    return result as T
}

/** 转换列表接口的响应体：{ list, total, ... } → 字段全量 snake_case */
function transformListResponse(raw: any) {
    if (!raw) return raw
    return {
        ...raw,
        list: (raw.list || []).map((item: any) => camelToSnake(item))
    }
}

/** 转换详情响应体 */
function transformItemResponse(raw: any) {
    if (!raw) return raw
    return camelToSnake(raw)
}

class AiService {
    /** 获取提供商类型枚举（供前端下拉，避免前后端各维护一份导致漂移） */
    public async listProviderTypes() {
        const result = await xhr({
            method: 'get',
            url: `${BASE}/types`,
            params: {}
        })
        return result
    }

    /** 模型列表（规范版分页接口，供模型下拉等轻量场景）：参数 page/size/keyword/type/status，返回 results（驼峰） */
    public async listProvidersPage(params: { page: number; size: number; type?: string; status?: number; keyword?: string }) {
        const result = await xhr({
            method: 'get',
            url: `${BASE}/page`,
            params: params
        })
        return result
    }

    /** 提供商列表查询（分页 + 关键词 + 状态过滤） */
    public async listProviders(params: IGetProviderListParams) {
        const result = await xhr({
            method: 'get',
            url: `${BASE}/list`,
            params: params
        })
        if (result.status === 200 && result.data) {
            result.data = transformListResponse(result.data)
        }
        return result
    }

    /** 提供商详情（密钥掩码） */
    public async getProvider(id: number) {
        const result = await xhr({
            method: 'get',
            url: `${BASE}/${id}`,
            params: {}
        })
        if (result.status === 200 && result.data) {
            result.data = transformItemResponse(result.data)
        }
        return result
    }

    /** 新建提供商（api_key 明文入参，服务端加密落库） */
    public async createProvider(params: IUpsertProviderParams) {
        const result = await xhr({
            method: 'post',
            url: BASE,
            params: params
        })
        return result
    }

    /** 编辑提供商（api_key 留空表示不修改） */
    public async updateProvider(params: IUpsertProviderParams) {
        const result = await xhr({
            method: 'put',
            url: BASE,
            params: params
        })
        return result
    }

    /** 删除提供商（已被业务功能绑定的不可删，后端返回明确错误） */
    public async deleteProvider(id: number) {
        const result = await xhr({
            method: 'delete',
            url: `${BASE}/${id}`,
            params: {}
        })
        return result
    }

    /** 启用/禁用（普通管理员即可） */
    public async setProviderStatus(id: number, status: number) {
        const result = await xhr({
            method: 'put',
            url: `${BASE}/${id}/status`,
            params: { status }
        })
        return result
    }

    /** 设为默认（全局唯一，事务内清旧置新） */
    public async setProviderDefault(id: number) {
        const result = await xhr({
            method: 'put',
            url: `${BASE}/${id}/default`,
            params: {}
        })
        return result
    }

    /** 根据类型获取可选能力列表 */
    public async getAvailableCapabilities(type: string) {
        const result = await xhr({
            method: 'get',
            url: '/api/ai/provider/available-capabilities',
            params: { type }
        })
        return result
    }
}

// 实例化后导出，全局单例
export default new AiService()
